import config from 'config';

import axios, { CancelToken } from 'client';
import * as dto from 'client/dto';
import { LocationIndexSearchCount } from 'utils/constants';

import { LocationSearchResult, LocationSearchType } from 'components/_controls/AoiSearch/models';
import { buildMultiTermSearchQuery } from 'utils/stringHelpers';

const axiosInstance = axios;

let searchLocationsCancel = null;

const headers = {
  'X-Entity-Type': 'Location',
};

function getUserLocations(): Promise<dto.GetUserLocationModel> {
  return axios.get(`/api/userlocation`, { headers }).then(({ data }) => data);
}

function addUserLocation(geonameId: number): Promise<dto.GetUserLocationModel> {
  return axios.post(`/api/userlocation`, { geonameId }, { headers }).then(({ data }) => data);
}

function updateUserLocations(geonameIds: number[]): Promise<dto.GetUserLocationModel> {
  return axios.put(`/api/userlocation`, geonameIds, { headers }).then(({ data }) => data);
}

function deleteUserLocation(geonameId: number): Promise<dto.GetUserLocationModel> {
  return axios.delete(`/api/userlocation/${geonameId}`, { headers }).then(({ data }) => data);
}

function locationAzureSearch(
  name: string,
  locationType: dto.LocationType,
  count: number
): Promise<LocationSearchResult[]> {
  return axios
    .get(`${config.searchIndexBaseUrl}${config.locationIndexUrl}`, {
      withCredentials: false,
      headers: {
        'api-key': config.locationIndexApiKey,
      },
      params: {
        'api-version': '2019-05-06',
        $top: count,
        $select:
          'id,latitude,locationType,longitude,displayName,countryName,name,provinceName,countryId',
        $filter: `locationType eq ${locationType} and isDuplicate eq false`,
        $orderby: `population desc`,
        search: buildMultiTermSearchQuery(name),
        searchFields: 'name,displayName',
      },
      cancelToken: new CancelToken(c => {
        searchLocationsCancel = c;
      }),
    })
    .then(({ data }) =>
      data.value.map(
        d =>
          ({
            id: parseInt(d.id, 10),
            displayName: d.displayName,
            locationType: d.locationType,
            name: d.name,
            countryName: d.countryName,
            provinceName: d.provinceName,
            countryId: d.countryId,
          } as LocationSearchResult)
      )
    );
}

function airportAzureSearch(name: string, count: number): Promise<LocationSearchResult[]> {
  return axios
    .get(`${config.searchIndexBaseUrl}${config.airportsIndexUrl}`, {
      withCredentials: false,
      headers: {
        'api-key': config.locationIndexApiKey,
      },
      params: {
        'api-version': '2020-06-30',
        $top: count,
        $select: 'locationId,code,name,displayName,cityName,provinceName,countryName,countryId',
        $filter: `isDuplicate eq false`,
        $orderby: `search.score() desc, totalVolume desc`,
        search: `code: ${name}^2 ${buildMultiTermSearchQuery(name)}`,
        searchFields: 'name, code, cityName',
        queryType: 'full',
      },
      cancelToken: new CancelToken(c => {
        searchLocationsCancel = c;
      }),
    })
    .then(({ data }) =>
      data.value.map(
        d =>
          ({
            id: parseInt(d.locationId, 10),
            locationType: LocationSearchType.Airport,
            displayName: d.displayName,
            name: d.name,
            countryName: d.countryName,
            provinceName: d.provinceName,
            airportCode: d.code,
            cityName: d.cityName,
            countryId: d.countryId,
          } as LocationSearchResult)
      )
    );
}

function searchLocations(
  name: string,
  numOfCountries = LocationIndexSearchCount.countries,
  numOfProvinces = LocationIndexSearchCount.provinces,
  numOfMetroAreas = LocationIndexSearchCount.metroAreas,
  numOfCounties = LocationIndexSearchCount.counties,
  numOfCities = LocationIndexSearchCount.cities,
  numOfAirports = 0
): Promise<LocationSearchResult[]> {
  searchLocationsCancel && searchLocationsCancel();

  const searchPromises = [];
  if (numOfCountries > 0) {
    searchPromises.push(locationAzureSearch(name, dto.LocationType.Country, numOfCountries));
  }

  if (numOfProvinces > 0) {
    searchPromises.push(locationAzureSearch(name, dto.LocationType.Province, numOfProvinces));
  }

  if (numOfMetroAreas > 0) {
    searchPromises.push(locationAzureSearch(name, dto.LocationType.MetroArea, numOfMetroAreas));
  }

  if (numOfCounties > 0) {
    searchPromises.push(locationAzureSearch(name, dto.LocationType.County, numOfCounties));
  }

  if (numOfCities > 0) {
    searchPromises.push(locationAzureSearch(name, dto.LocationType.City, numOfCities));
  }

  if (numOfAirports > 0) {
    searchPromises.push(airportAzureSearch(name, numOfAirports));
  }

  return Promise.all(searchPromises).then(data => {
    return [].concat(...data);
  });
}

function getGeoname(model: dto.GetGeonameShapesModel): Promise<dto.GetGeonameModel[]> {
  searchLocationsCancel && searchLocationsCancel();
  return axiosInstance.post(`/api/geoname`, model, {}).then(({ data }) => data);
}

function searchCity(name: string): Promise<LocationSearchResult[]> {
  return locationAzureSearch(name, dto.LocationType.City, 10);
}

export default {
  getUserLocations,
  updateUserLocations,
  addUserLocation,
  deleteUserLocation,
  searchLocations,
  searchCity,
  getGeoname,
};
